import React, { useState, useRef } from 'react';
import { Upload, Link as LinkIcon, X, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

// Converts image file to base64. Compresses to keep size reasonable.
const fileToCompressedBase64 = (file, maxWidth = 1600, quality = 0.82) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const ImageUpload = ({ value, onChange, label = 'Imagen', testId = 'image-upload' }) => {
  const [mode, setMode] = useState('upload'); // 'upload' or 'url'
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsProcessing(true);
    try {
      const base64 = await fileToCompressedBase64(file);
      onChange(base64);
    } catch (err) {
      console.error('Error processing image', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-white/70 text-sm font-medium">{label}</label>
        <div className="flex gap-1 bg-[#0A1628] rounded-lg p-1">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              mode === 'upload' ? 'bg-[#60A5FA] text-white' : 'text-white/60'
            }`}
          >
            <Upload className="w-3 h-3 inline mr-1" />
            Subir
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              mode === 'url' ? 'bg-[#60A5FA] text-white' : 'text-white/60'
            }`}
          >
            <LinkIcon className="w-3 h-3 inline mr-1" />
            URL
          </button>
        </div>
      </div>

      {/* Preview */}
      {value && (
        <div className="relative inline-block">
          <img
            src={value}
            alt="preview"
            className="h-32 w-auto rounded-lg border border-white/20 object-cover"
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      )}

      {mode === 'upload' ? (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            data-testid={`${testId}-file`}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            className="w-full border-2 border-dashed border-white/20 rounded-xl py-6 flex flex-col items-center justify-center text-white/60 hover:border-[#60A5FA]/50 hover:text-white transition-colors"
          >
            {isProcessing ? (
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
            ) : (
              <Upload className="w-6 h-6 mb-2" />
            )}
            <span className="text-sm">{isProcessing ? 'Procesando...' : 'Haz clic para subir imagen'}</span>
          </button>
        </div>
      ) : (
        <Input
          type="text"
          value={value && value.startsWith('data:') ? '' : value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://ejemplo.com/imagen.jpg"
          data-testid={`${testId}-url`}
          className="bg-[#0A1628]/60 border-white/20 text-white focus:border-[#60A5FA] rounded-xl"
        />
      )}
    </div>
  );
};

export default ImageUpload;
